import { useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  NativeSelect,
  OutlinedInput,
  Stack,
  TextField,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useLocation, useNavigate } from 'react-router-dom';
import { Ti18nId } from 'hooks/useI18n';
import { setSearchQuery } from 'utils/searchQuery';
import { TApprove } from './api/type';
import { formatWithComma } from 'utils/formatting';
import { IAdminPatients } from './api/type';
import useNotification from 'hooks/useNotification';
import { getAccountsPatient, postPrivate, postPublic } from './api/index';
import useUser from 'store/user/useUser';

interface Props {
  totalCount: number;
  onRequired: (id: Ti18nId) => void;
  currentSearchType?: number;
  currentApprove: string | TApprove | null;
  selected: string[];
  list: IAdminPatients[];
  handleAddFriend: () => void;
  setList: React.Dispatch<React.SetStateAction<IAdminPatients[]>>;
  setTotalCount: React.Dispatch<React.SetStateAction<number>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const ListHeader = ({
  totalCount,
  onRequired,
  currentSearchType,
  currentApprove,
  list,
  selected,
  handleAddFriend,
  setList,
  setTotalCount,
  setIsLoading,
}: Props) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { onSuccess, onFail, onResultCode } = useNotification();
  const { student_uuid: userId } = useUser();

  const searchTypeRef = useRef<HTMLSelectElement>(null);
  const keywordRef = useRef<HTMLInputElement>(null);

  const onSearch = () => {
    const keyword = keywordRef.current?.value;
    const patient_id = Number(keywordRef.current?.value);
    const college = Number(keywordRef.current?.value);

    const searchType = searchTypeRef.current?.value;
    if (!keyword) return onRequired('REQUIRED.KEYWORD');

    if (searchType === '2' && isNaN(Number(keyword)))
      return onRequired('REQUIRED.NUMBER');
    if (searchType === '1' && !isNaN(Number(keyword)))
      return onRequired('REQUIRED.STRING');

    const query = isNaN(Number(keyword))
      ? setSearchQuery({
          page: 1,
          approve: currentApprove,
          searchType,
          keyword,
        })
      : searchType === '2'
      ? setSearchQuery({
          page: 1,
          approve: currentApprove,
          searchType,
          keyword: patient_id,
        })
      : setSearchQuery({
          page: 1,
          approve: currentApprove,
          searchType,
          patient_id: '',
          keyword: '',
          college,
        });
    navigate(`${pathname}?${query}`);
  };

  const searchReset = () => {
    getAccountsPatient({
      page: '1',
      keyword: '',
      searchType: '2',
      user_id: String(userId),
    })
      .then(({ data }) => {
        setList(data.patients);
        setTotalCount(data.count);
      })
      .catch(e => {
        const message = `Виртуал өвчтөний жагсаалтыг ачаалах амжилтгүй боллоо.`;
        onFail(message, e);
      })
      .finally(() => setIsLoading(false));

    navigate('/professorAuth/list');
  };

  const rows = list.map(item => ({ ...item, id: item.patient_id }));
  const changeNumber = selected.map(a => Number(a));
  const selectedIDs = new Set(changeNumber);
  const selectedRows = rows.filter(r => selectedIDs.has(r.id));

  const publicButton = () => {
    const privatePatients = selectedRows
      .map(a => a.patient_private)
      .filter(a => a);
    const publicPatients = selectedRows
      .map(a => a.patient_private)
      .filter(a => !a);

    if (publicPatients.length > 0) {
      return alert('Зөвхөн хувийн өвчтөнүүдийг сонгоно уу.');
    }
    if (privatePatients.length > 0) {
      let selectedPublic = selectedRows
        .filter(r => r.patient_private)
        .map(a => a.patient_id);

      postPublic({
        patient_id: selectedPublic.join(','),
        user_id: userId,
        setType: 'public',
      })
        .then(({ data: { rc } }) => {
          if (rc === 111) return onResultCode(rc);

          const message = `${selected.length} виртуал өвчтөнийг оюутнуудад (ENR) нийтэд нээлттэй болголоо.`;
          onSuccess(message);
          handleAddFriend();
        })
        .catch(e => {
          const message = `${selected.length} виртуал өвчтөнийг оюутнуудад (ENR) нийтэд нээлттэй болгох амжилтгүй боллоо.`;
          onFail(message, e);
        });
    }
  };

  const privateButton = () => {
    const privateOnes = selectedRows.map(a => a.patient_private).filter(a => a);
    const publicOnes = selectedRows.map(a => a.patient_private).filter(a => !a);

    if (publicOnes.length > 0) {
      let selectedPublic = selectedRows
        .filter(r => !r.patient_private)
        .map(a => a.patient_id);

      postPublic({
        patient_id: selectedPublic.join(','),
        user_id: userId,
        setType: 'private',
      })
        .then(({ data: { rc } }) => {
          if (rc === 111) return onResultCode(rc);

          const message = `${selected.length} виртуал өвчтөнийг хувийн (ENR нуугдмал) болголоо.`;
          onSuccess(message);
          handleAddFriend();
        })
        .catch(e => {
          const message = `${selected.length} виртуал өвчтөнийг хувийн (ENR нуугдмал) болгох амжилтгүй боллоо.`;
          onFail(message, e);
        });
    }
    if (privateOnes.length > 0) {
      return alert('Зөвхөн нийтийн өвчтөнүүдийг сонгоно уу.');
    }
  };

  const ActionButton = () => (
    <Stack direction={'row'} spacing={2}>
      <LoadingButton variant="contained" onClick={() => publicButton()}>
        ENR Нийтэд Нээлттэй Болгох
      </LoadingButton>
      <LoadingButton
        variant="contained"
        color="error"
        onClick={() => privateButton()}
      >
        ENR Хувийн Болгох
      </LoadingButton>
    </Stack>
  );

  const SearchKeyword = () => (
    <Stack direction={'row'} spacing={0.5} alignItems="center">
      <NativeSelect
        size="small"
        input={<OutlinedInput />}
        inputRef={searchTypeRef}
        defaultValue={currentSearchType}
      >
        <option value={1}>Нэр</option>
        <option value={2}>ID</option>
      </NativeSelect>
      <TextField
        label="Хайх"
        size="small"
        inputRef={keywordRef}
        sx={{ input: { minHeight: 40, boxSizing: 'border-box' } }}
      />
      <Button variant="contained" onClick={onSearch}>
        Хайх
      </Button>
      <Button variant="contained" onClick={searchReset}>
        Хайлтыг Цэвэрлэх
      </Button>
    </Stack>
  );

  return (
    <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mr: 'auto' }}>
        Нийт {formatWithComma(totalCount)}
      </Typography>
      <Stack direction="row" spacing={2}>
        <ActionButton />
        <Divider sx={{ borderColor: 'gray' }} />
        <SearchKeyword />
      </Stack>
    </Box>
  );
};

export default ListHeader;
